/**
 * Benefit definitions for Short-Term Disability (STD)
 *
 * Defines the structure, labels, and cell specs for each STD benefit field.
 * Uses shared cell specs from CELL_SPECS and leverages builder patterns where applicable.
 */

import {
  addNotDisclosed,
  addNotDisclosedAndNotOffered,
  BaseBenefitPlanPolicyPeriod,
  BenefitAmountTypeEnum,
  BillableTypeEnum,
  DefinitionOfDisabilityEnum,
  DefinitionOfEarningsEnum,
  EliminationPeriodTypeEnum,
  EmployerContributionEnum,
  extendEnumWithNotDisclosed,
  extendEnumWithNotDisclosedAndNotOffered,
  FundingTypeEnum,
  MaximumAmountTypeEnum,
  NotDisclosed,
  NotOffered,
  RateGuaranteeTypeEnum,
  RfpStdBenefitDurationTypeEnum,
} from "@platform/openapi-types";

import { mpOnlyRow } from "../shared/definitions/mpOnlyRow";
import { BenefitDefinitions } from "../../benefits/benefit.types";
import {
  createEnumFormatter,
  createValueFormatter,
} from "../../components/PlanEditor/utilities/enumFormatters";
import * as sharedCellSpecs from "../shared/cellSpecs/common";
import {
  buildPreExistingConditionBenefitPair,
  buildTypeAmountBenefitPair,
  buildTypeMultipleAmountsBenefit,
  buildCommissionBenefits,
} from "../shared/definitions/builders";
import {
  definitionOfDisabilityLabels,
  definitionOfEarningsLabels,
  employerContributionLabels,
} from "../shared/definitions/customLabels";

// Pre-build commission definitions to override isRequired on type fields
const commissionDefs = buildCommissionBenefits();

// Pre-build the Minimum Benefit type/amount pair to override mpOnly, which
// buildTypeAmountBenefitPair doesn't take as a config option.
const minimumBenefitDefs = buildTypeAmountBenefitPair({
  prefix: "minimumBenefit",
  typeLabel: "Minimum Benefit",
  typeCell: sharedCellSpecs.enumDropdown,
  typeEnumValues: sharedCellSpecs.minimumBenefitTypeValues,
  typeCustomLabels: sharedCellSpecs.minimumBenefitTypeLabels,
  amountLabel: "Amount",
  amountCell: sharedCellSpecs.dollarAmount,
  typeDescription: "Type of minimum benefit structure",
  amountDescription: "Minimum benefit amount",
  disabledValues: ["notDisclosed"],
  mpConfigKey: "minimum_benefit",
  amountCellVariants: {
    options: {
      percentageOfSalary: sharedCellSpecs.percentageAmount,
    },
    default: sharedCellSpecs.dollarAmount,
  },
});

export const stdBenefitDefinitions: BenefitDefinitions = {
  currentCarrierName: {
    label: "Current Carrier",
    description:
      "Current carrier name (disabled if policy period = new or alternate)",
    cell: sharedCellSpecs.currentCarrierName,
    isRequired: true,
    networkDependent: false,
    benefitPlanLevel: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  ...commissionDefs,
  currentCommissionType: {
    ...commissionDefs["currentCommissionType"],
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  requestedCommissionType: {
    ...commissionDefs["requestedCommissionType"],
    isRequired: true,
  },
  eligibilityClasses: {
    label: "Eligibility Class",
    description: "Benefit classes assigned to this benefit plan group",
    cell: sharedCellSpecs.eligibilityClasses,
    benefitPlanLevel: true,
    isRequired: true,
  },
  fundingType: {
    label: "Funding Type",
    description: "Funding arrangement for the benefit plan",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        allowTyping: true,
        values: Object.values(FundingTypeEnum),
        formatValue: createEnumFormatter(),
      },
      valueFormatter: createValueFormatter(),
    },
    isRequired: true,
    requiredOnCurrentCoverageOnly: true,
    networkDependent: false,
    benefitPlanLevel: true,
  },
  benefitAmountType: {
    label: "Benefit Amount",
    description: "Type of benefit amount calculation",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(BenefitAmountTypeEnum),
      },
    },
    networkDependent: false,
    isRequired: true,
    refreshesCells: ["benefitAmountAmount", "benefitAmountMinimum"],
  },
  benefitAmountAmount: {
    label: "Amount",
    description: "Benefit amount value (dollar or percentage based on type)",
    cell: sharedCellSpecs.dollarAmount, // Default cell spec
    nested: true,
    networkDependent: false,
    defaultValue: 0,
    editableWhen: {
      dependsOn: "benefitAmountType",
      disabledValues: [NotDisclosed],
    },
    cellVariants: {
      dependsOn: "benefitAmountType",
      options: {
        increments: sharedCellSpecs.dollarAmount,
        percentageOfSalary: sharedCellSpecs.percentageAmount,
      },
      default: sharedCellSpecs.dollarAmount,
    },
  },
  benefitAmountMinimum: {
    label: "Minimum",
    description: "Minimum benefit amount",
    cell: sharedCellSpecs.dollarAmount,
    nested: true,
    networkDependent: false,
    defaultValue: 0,
    editableWhen: {
      dependsOn: "benefitAmountType",
      disabledValues: addNotDisclosed([
        BenefitAmountTypeEnum.percentageOfSalary,
      ]),
    },
  },
  ...buildTypeMultipleAmountsBenefit({
    prefix: "maximumAmount",
    typeLabel: "Maximum Amount",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosed(MaximumAmountTypeEnum),
    typeDescription: "Type of maximum benefit amount",
    isRequired: true,
    amountFields: [
      {
        suffix: "DollarAmount",
        label: "Dollar",
        cell: sharedCellSpecs.dollarAmount,
        description: "Maximum amount in dollars",
        defaultValue: 0,
        disabledValues: ["unlimited", NotDisclosed],
      },
      {
        suffix: "Percent",
        label: "Percentage",
        cell: sharedCellSpecs.percentageAmount,
        description: "Maximum amount as percentage",
        defaultValue: 0,
        disabledValues: ["dollarAmount", "unlimited", NotDisclosed],
      },
    ],
  }),
  definitionOfDisability: {
    label: "Definition of Disability",
    mpConfigKey: "definition_of_disability",
    description: "How disability is defined for eligibility",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(DefinitionOfDisabilityEnum),
        formatValue: createEnumFormatter(definitionOfDisabilityLabels),
      },
      valueFormatter: createValueFormatter(definitionOfDisabilityLabels),
    },
    networkDependent: false,
  },
  ...buildTypeAmountBenefitPair({
    prefix: "eliminationPeriodAccident",
    typeLabel: "Elimination Period - Accident",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      EliminationPeriodTypeEnum,
    ),
    amountLabel: "Number of Days",
    amountCell: sharedCellSpecs.numberField,
    typeDescription: "Type of waiting period for accident",
    amountDescription:
      "Waiting period in days before benefits begin for accident",
    valueSuffix: "Days",
    isRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "eliminationPeriodIllness",
    typeLabel: "Elimination Period - Illness",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      EliminationPeriodTypeEnum,
    ),
    amountLabel: "Number of Days",
    amountCell: sharedCellSpecs.numberField,
    typeDescription: "Type of waiting period for illness",
    amountDescription:
      "Waiting period in days before benefits begin for illness",
    valueSuffix: "Days",
    isRequired: true,
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "benefitDuration",
    typeLabel: "Benefit Duration",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpStdBenefitDurationTypeEnum,
    ),
    amountLabel: "Amount",
    amountCell: sharedCellSpecs.numberField,
    typeDescription: "Unit of time for benefit duration",
    amountDescription: "Maximum length of time benefits are paid",
    amountDefaultValue: 1,
    isRequired: true,
  }),
  ...buildPreExistingConditionBenefitPair({
    typeDescription: "Type of pre-existing condition limitation",
    monthsDetailDescription:
      "Configure months diagnosis free before/after effective date and treatment free. Format: XX/XX or XX/XX/XX",
    disabledValues: ["waived", "none", NotDisclosed, NotOffered],
    monthsDetailDefaultValue: "01/01",
    includeNotOffered: true,
    mpConfigKey: "pre_existing_condition",
  }),
  ...buildTypeAmountBenefitPair({
    prefix: "definitionOfEarnings",
    mpConfigKey: "definition_of_earnings",
    typeLabel: "Definition of Earnings",
    typeCell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(DefinitionOfEarningsEnum),
        formatValue: createEnumFormatter(definitionOfEarningsLabels),
      },
      valueFormatter: createValueFormatter(definitionOfEarningsLabels),
    },
    amountLabel: "Average Month",
    amountCell: sharedCellSpecs.numberField,
    typeDescription: "What earnings are considered for benefit calculation",
    amountDescription: "Number of months to average earnings",
    // Blank, not 1: average months is optional, and 0 is a real value.
    amountDefaultValue: null,
    valueSuffix: "AverageMonths",
    disabledValues: addNotDisclosed([
      DefinitionOfEarningsEnum.salary,
      DefinitionOfEarningsEnum.k1,
      DefinitionOfEarningsEnum.priorYearW2,
      DefinitionOfEarningsEnum.notApplicable,
      DefinitionOfEarningsEnum.notIncluded,
    ]),
  }),
  ficaMatch: {
    label: "FICA Match",
    mpConfigKey: "fica_match",
    description: "Whether employer FICA match is included",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: addNotDisclosedAndNotOffered(["included", "notIncluded"]),
      },
    },
    networkDependent: false,
  },
  billableType: {
    label: "Billable or Non-Billable",
    mpConfigKey: "billable_or_non_billable",
    description: "How premiums are billed",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosed(BillableTypeEnum),
      },
    },
    networkDependent: false,
  },
  employerContribution: {
    label: "Employer Contribution",
    description: "Defines employer vs employee contribution structure",
    cell: {
      ...sharedCellSpecs.employerContribution,
      cellEditorParams: {
        ...sharedCellSpecs.employerContribution.cellEditorParams,
        values: extendEnumWithNotDisclosed(EmployerContributionEnum),
        formatValue: createEnumFormatter(employerContributionLabels),
      },
      valueFormatter: createValueFormatter(employerContributionLabels),
    },
    networkDependent: false,
    isRequired: true,
  },
  participationRequirements: {
    label: "Participation Requirements",
    description: "Participation requirement details for the benefit plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
    benefitPlanLevel: true,
    requestedPlanRowVisibility: {
      field: "employerContribution",
      equals: EmployerContributionEnum.voluntary,
    },
  },
  ...buildTypeAmountBenefitPair({
    prefix: "rateGuarantee",
    typeLabel: "Rate Guarantee",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: Object.keys(RateGuaranteeTypeEnum),
    amountCell: sharedCellSpecs.numberField,
    typeDescription:
      "Time frame in which rates are guaranteed to stay the same",
    amountDescription: "Number of months or years the rate is guaranteed",
    amountDefaultValue: 1,
    disabledValues: [
      RateGuaranteeTypeEnum.notIncluded,
      RateGuaranteeTypeEnum.notOffered,
    ],
  }),
  enrolledLives: mpOnlyRow("Enrolled Lives", "enrolled_lives"),

  // Extended (non-v5) fields (CD-2068). Backend fields are plain
  // strings/floats for now (RFC-004), not yet enums, so type fields are text
  // rather than dropdowns and amount fields aren't gated by editableWhen,
  // except eligibilityHours (CD-2325) and minimumBenefit (CD-2552).
  eligibilityHours: {
    mpOnly: true,
    label: "Eligibility Hours",
    mpConfigKey: "eligibility_hours",
    description: "Minimum hours worked per week to be eligible for the plan",
    cell: sharedCellSpecs.eligibilityHoursDropdown,
    networkDependent: false,
  },
  minimumBenefitType: {
    ...minimumBenefitDefs.minimumBenefitType,
    mpOnly: true,
  },
  minimumBenefitAmount: {
    ...minimumBenefitDefs.minimumBenefitAmount,
    mpOnly: true,
  },
  firstDayHospital: {
    mpOnly: true,
    label: "First Day Hospital",
    mpConfigKey: "first_day_hospital",
    description: "First-day-hospital benefit details",
    cell: sharedCellSpecs.includedNotIncludedDropdown,
    networkDependent: false,
  },
  // Partial Disability Formula is a free-text field per the spreadsheet
  // (Data Type "text", no value list) — not part of the CD-2325 dropdown
  // gap, so it stays text.
  partialDisabilityFormula: {
    mpOnly: true,
    label: "Partial Disability Formula",
    mpConfigKey: "partial_disability_formula",
    description: "Formula used to calculate partial disability benefits",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  partialDisability: {
    mpOnly: true,
    label: "Partial Disability",
    mpConfigKey: "partial_disability",
    description: "Partial disability benefit details",
    cell: sharedCellSpecs.partialDisabilityDropdown,
    networkDependent: false,
  },
  earningsTest: {
    mpOnly: true,
    label: "Earnings Test",
    mpConfigKey: "earnings_test",
    description: "Earnings test benefit details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  portability: {
    mpOnly: true,
    label: "Portability",
    mpConfigKey: "portability",
    description: "Portability benefit details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  rehabilitativeIncentiveBenefit: {
    mpOnly: true,
    label: "Rehabilitative Incentive Benefit",
    mpConfigKey: "rehabilitative_incentive_benefit",
    description: "Rehabilitative incentive benefit details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  recurrentDisability: {
    mpOnly: true,
    label: "Recurrent Disability",
    mpConfigKey: "recurrent_disability",
    description: "Recurrent disability benefit details",
    cell: sharedCellSpecs.text,
    networkDependent: false,
  },
  w2Prep: {
    mpOnly: true,
    label: "W-2 Prep",
    mpConfigKey: "w_2_prep",
    description: "W-2 preparation service details",
    cell: sharedCellSpecs.w2PrepDropdown,
    networkDependent: false,
  },
  claimsIntake: {
    mpOnly: true,
    label: "Claims Intake",
    mpConfigKey: "claims_intake",
    description: "Claims intake process details",
    cell: sharedCellSpecs.claimsIntakeDropdown,
    networkDependent: false,
  },
  statutoryDiIntegration: {
    mpOnly: true,
    label: "Statutory DI integration",
    mpConfigKey: "statutory_di_integration",
    description: "Statutory disability insurance integration details",
    cell: sharedCellSpecs.diIntegrationDropdown,
    networkDependent: false,
  },
  pflIntegration: {
    mpOnly: true,
    label: "PFL integration",
    mpConfigKey: "pfl_integration",
    description: "Paid Family Leave integration details",
    cell: sharedCellSpecs.diIntegrationDropdown,
    networkDependent: false,
  },
};
